import React, { useState } from "react";
import { Category } from "../types";
import { BudgetProgress, BudgetStatus } from "../selectors";
import { formatMoney, parseAmount } from "../format";

interface BudgetListProps {
    items: BudgetProgress[];
    categories: Category[];
    currency: string;
    onSetBudget: (categoryId: string, limit: number | null) => void;
}

// Status always ships with an icon and a label, never color alone
const STATUS: Record<BudgetStatus, { icon: string; label: string }> = {
    good: { icon: "✓", label: "On track" },
    warning: { icon: "!", label: "Close to limit" },
    critical: { icon: "✕", label: "Over budget" },
};

export function BudgetList({ items, categories, currency, onSetBudget }: BudgetListProps) {
    const [editing, setEditing] = useState<string | null>(null);
    const [draft, setDraft] = useState("");
    const [adding, setAdding] = useState("");

    const unbudgeted = categories.filter(c => c.type === "expense" && !items.some(i => i.category.id === c.id));

    const startEdit = (categoryId: string, limit: number) => {
        setEditing(categoryId);
        setDraft(String(limit / 100));
    };

    const saveEdit = () => {
        const limit = parseAmount(draft);
        if (editing && limit !== null) {
            onSetBudget(editing, limit);
        }
        setEditing(null);
    };

    return (
        <section className="fin-card">
            <div className="fin-card-head">
                <h3>Monthly budgets</h3>
            </div>
            {items.length === 0 && <p className="fin-empty">No budgets yet. Add one below.</p>}
            <ul className="fin-budgets">
                {items.map(({ category, budget, spent, ratio, status }) => (
                    <li key={category.id}>
                        <div className="fin-budget-top">
                            <span className="fin-budget-name">
                                <span aria-hidden="true">{category.icon}</span> {category.name}
                            </span>
                            <span className={`fin-status ${status}`}>
                                <span aria-hidden="true">{STATUS[status].icon}</span> {STATUS[status].label}
                            </span>
                        </div>
                        <div className="fin-budget-track" role="progressbar" aria-valuenow={Math.round(ratio * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={`${category.name} budget used`}>
                            <span className={`fin-budget-bar ${status}`} style={{ width: `${Math.min(100, ratio * 100)}%` }} />
                        </div>
                        <div className="fin-budget-bottom">
                            {editing === category.id ? (
                                <form
                                    className="fin-inline-form"
                                    onSubmit={e => {
                                        e.preventDefault();
                                        saveEdit();
                                    }}
                                >
                                    <input
                                        autoFocus
                                        inputMode="decimal"
                                        value={draft}
                                        onChange={e => setDraft(e.target.value)}
                                        aria-label={`${category.name} monthly limit`}
                                    />
                                    <button type="submit">Save</button>
                                    <button type="button" className="danger" onClick={() => { onSetBudget(category.id, null); setEditing(null); }}>
                                        Remove
                                    </button>
                                </form>
                            ) : (
                                <>
                                    <span>
                                        {formatMoney(spent, currency)} of {formatMoney(budget.limit, currency)}
                                        {spent <= budget.limit && <small> · {formatMoney(budget.limit - spent, currency)} left</small>}
                                    </span>
                                    <button className="fin-link" onClick={() => startEdit(category.id, budget.limit)}>Edit</button>
                                </>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
            {unbudgeted.length > 0 && (
                <div className="fin-add-budget">
                    <select value={adding} onChange={e => setAdding(e.target.value)} aria-label="Category for new budget">
                        <option value="">+ Add a budget…</option>
                        {unbudgeted.map(c => (
                            <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                        ))}
                    </select>
                    {adding && (
                        <button
                            className="fin-link"
                            onClick={() => {
                                onSetBudget(adding, 200_00);
                                startEdit(adding, 200_00);
                                setAdding("");
                            }}
                        >
                            Add
                        </button>
                    )}
                </div>
            )}
        </section>
    );
}
